import { z } from 'zod'
import { DEFAULT_LOCALE, interpolate, type Locale, type Localized } from './i18n.ts'
import { formatQuantity, formatQuantityWithUnit, getUnit } from './units.ts'

/**
 * Bulk price tiers, shared by client and server.
 *
 * This lives in @hm/contracts rather than @hm/core precisely so there is exactly
 * ONE implementation of tier resolution. The client uses it to preview a price as
 * the buyer types a quantity; the server uses it as the authority when composing
 * and accepting an offer. Two implementations would eventually disagree, and the
 * disagreement would be about money.
 *
 * `minQty`/`maxQty` are scaled by 10^unit.decimals like every other quantity.
 * `maxQty === null` means "and above" — the open-ended top tier.
 */

export interface PriceTier {
  minQty: number
  maxQty: number | null
  unitPriceMinor: number
  label?: string | null
}

export const priceTierSchema = z.object({
  minQty: z.number().int().min(0),
  maxQty: z.number().int().min(1).nullable(),
  unitPriceMinor: z.number().int().min(0),
  label: z.string().max(60).nullish(),
})

/** Spelled out again in the message below, not interpolated: the guard test in
 *  validation-messages.test.ts reads literals out of the source. */
export const MAX_PRICE_TIERS = 12

export const priceTierListSchema = z
  .array(priceTierSchema)
  .min(1, 'Add at least one price row.')
  .max(MAX_PRICE_TIERS, 'One item can have 12 price rows at most.')

/** The tier that applies at `qty`, or null if the quantity is below the first tier. */
export function resolveTier(tiers: readonly PriceTier[], qty: number): PriceTier | null {
  let best: PriceTier | null = null
  for (const t of tiers) {
    if (qty < t.minQty) continue
    if (t.maxQty !== null && qty > t.maxQty) continue
    // Ties go to the more specific (higher minQty) tier.
    if (!best || t.minQty > best.minQty) best = t
  }
  return best
}

/** Unit price at `qty`, falling back to the lowest tier when below the first break. */
export function unitPriceAt(tiers: readonly PriceTier[], qty: number): number | null {
  const tier = resolveTier(tiers, qty)
  if (tier) return tier.unitPriceMinor
  const sorted = sortTiers(tiers)
  return sorted[0]?.unitPriceMinor ?? null
}

export function sortTiers(tiers: readonly PriceTier[]): PriceTier[] {
  return [...tiers].sort((a, b) => a.minQty - b.minQty)
}

export type TierIssueLevel = 'error' | 'warning'

export const TIER_ISSUE_CODES = [
  'empty',
  'overlap',
  'gap',
  'inverted-range',
  'multiple-open-ended',
  'open-ended-not-last',
  'no-open-ended',
  'duplicate-min',
  'price-not-decreasing',
  'below-moq',
] as const
export type TierIssueCode = (typeof TIER_ISSUE_CODES)[number]

export interface TierIssue {
  level: TierIssueLevel
  /** Index in the sorted tier list, or -1 for whole-list issues. */
  index: number
  code: TierIssueCode
  /**
   * Quantities the wording refers to, unformatted. The ladder editor renders them
   * in the reader's language; `message` below is the same sentence in English, kept
   * because the server puts it in a field error and a log line before it knows who
   * is going to read it.
   */
  params?: { unitCode: string; a?: number; b?: number }
  message: string
}

const TIER_ISSUE_MESSAGES: Localized<Record<TierIssueCode, string>> = {
  en: {
    empty: 'Add at least one price row.',
    overlap: 'This row overlaps the one above it.',
    gap: 'Nothing is priced between {a} and {b}.',
    'inverted-range': '"Up to" ({a}) cannot be less than "from" ({b}).',
    'multiple-open-ended': 'Only the last row can be open-ended.',
    'open-ended-not-last': 'The open-ended row must come last.',
    'no-open-ended': 'The last row must be open-ended, so any large order still has a price.',
    'duplicate-min': 'Two rows both start at {a}.',
    'price-not-decreasing': 'Price goes up for a larger quantity. Is that intended?',
    'below-moq': 'First row starts below the minimum order of {a}.',
  },
  ne: {
    empty: 'कम्तीमा एउटा रेट पङ्क्ति हाल्नुहोस्।',
    overlap: 'यो पङ्क्ति माथिको सँग जुधेको छ।',
    gap: '{a} र {b} बिचको कुनै रेट छैन।',
    'inverted-range': '"सम्म" ({a}) चाहिँ "देखि" ({b}) भन्दा कम हुन मिल्दैन।',
    'multiple-open-ended': 'अन्तिम पङ्क्ति मात्र खुला छोड्न मिल्छ।',
    'open-ended-not-last': 'खुला पङ्क्ति सबैभन्दा तल हुनुपर्छ।',
    'no-open-ended': 'अन्तिम पङ्क्ति खुला हुनुपर्छ, ठुलो अर्डरको पनि रेट निस्कियोस्।',
    'duplicate-min': 'दुई पङ्क्ति दुवै {a} देखि सुरु भएका छन्।',
    'price-not-decreasing': 'बढी परिमाणमा रेट बढेको छ। यही ठिक हो?',
    'below-moq': 'पहिलो पङ्क्ति न्यूनतम अर्डर {a} भन्दा तल सुरु भएको छ।',
  },
}

/** The issue in the reader's language, with its quantities formatted for that language. */
export function tierIssueText(issue: TierIssue, locale: Locale = DEFAULT_LOCALE): string {
  if (locale === DEFAULT_LOCALE) return issue.message
  const unitCode = issue.params?.unitCode
  const format = (n: number | undefined) =>
    n === undefined || !unitCode ? undefined : formatQuantityWithUnit(n, unitCode, locale)
  return interpolate(TIER_ISSUE_MESSAGES[locale][issue.code], {
    ...(format(issue.params?.a) !== undefined ? { a: format(issue.params?.a) as string } : {}),
    ...(format(issue.params?.b) !== undefined ? { b: format(issue.params?.b) as string } : {}),
  })
}

/**
 * Structural validation of a tier ladder. Errors block saving; warnings are shown
 * inline but allowed, because "price goes up with volume" is unusual rather than
 * impossible (a shop may genuinely charge more for a special large batch).
 */
export function validateTiers(
  tiers: readonly PriceTier[],
  opts: { unitCode: string; moq?: number },
): TierIssue[] {
  const issues: TierIssue[] = []
  const { unitCode, moq } = opts
  const u = getUnit(unitCode)
  const q = (n: number) => `${formatQuantity(n, unitCode)} ${u.symbol}`

  if (tiers.length === 0) {
    return [{ level: 'error', index: -1, code: 'empty', message: 'Add at least one price row.' }]
  }

  const sorted = sortTiers(tiers)

  const openEnded = sorted.map((t, i) => (t.maxQty === null ? i : -1)).filter((i) => i >= 0)
  if (openEnded.length === 0) {
    issues.push({
      level: 'error',
      index: sorted.length - 1,
      code: 'no-open-ended',
      message: 'The last row must be open-ended, so any large order still has a price.',
    })
  } else if (openEnded.length > 1) {
    issues.push({
      level: 'error',
      index: openEnded[1] ?? -1,
      code: 'multiple-open-ended',
      message: 'Only the last row can be open-ended.',
    })
  } else if (openEnded[0] !== sorted.length - 1) {
    issues.push({
      level: 'error',
      index: openEnded[0] ?? -1,
      code: 'open-ended-not-last',
      message: 'The open-ended row must come last.',
    })
  }

  for (const [i, t] of sorted.entries()) {
    if (t.maxQty !== null && t.maxQty < t.minQty) {
      issues.push({
        level: 'error',
        index: i,
        code: 'inverted-range',
        params: { unitCode, a: t.maxQty, b: t.minQty },
        message: `"Up to" (${q(t.maxQty)}) cannot be less than "from" (${q(t.minQty)}).`,
      })
    }
    if (moq != null && i === 0 && t.minQty < moq) {
      issues.push({
        level: 'warning',
        index: 0,
        code: 'below-moq',
        params: { unitCode, a: moq },
        message: `First row starts below the minimum order of ${q(moq)}.`,
      })
    }

    const prev = sorted[i - 1]
    if (!prev) continue

    if (prev.minQty === t.minQty) {
      issues.push({
        level: 'error',
        index: i,
        code: 'duplicate-min',
        params: { unitCode, a: t.minQty },
        message: `Two rows both start at ${q(t.minQty)}.`,
      })
    } else if (prev.maxQty === null || t.minQty <= prev.maxQty) {
      issues.push({
        level: 'error',
        index: i,
        code: 'overlap',
        message: `This row overlaps the one above it.`,
      })
    } else if (t.minQty > prev.maxQty + 1) {
      issues.push({
        level: 'error',
        index: i,
        code: 'gap',
        params: { unitCode, a: prev.maxQty, b: t.minQty },
        message: `Nothing is priced between ${q(prev.maxQty)} and ${q(t.minQty)}.`,
      })
    }

    if (t.unitPriceMinor > prev.unitPriceMinor) {
      issues.push({
        level: 'warning',
        index: i,
        code: 'price-not-decreasing',
        message: 'Price goes up for a larger quantity. Is that intended?',
      })
    }
  }

  return issues
}

export function hasTierErrors(issues: readonly TierIssue[]): boolean {
  return issues.some((i) => i.level === 'error')
}

/**
 * Build a contiguous ladder from partial input: fills each row's `maxQty` from the
 * next row's `minQty`, and opens the last row. Lets the tier editor ask only for
 * "from quantity" and "price", which is two fields instead of three.
 */
export function normalizeTiers(
  rows: readonly { minQty: number; unitPriceMinor: number; label?: string | null }[],
): PriceTier[] {
  const sorted = [...rows].sort((a, b) => a.minQty - b.minQty)
  return sorted.map((row, i) => {
    const next = sorted[i + 1]
    return {
      minQty: row.minQty,
      maxQty: next ? next.minQty - 1 : null,
      unitPriceMinor: row.unitPriceMinor,
      label: row.label ?? null,
    }
  })
}

/** Discount of a tier against the first (smallest-quantity) tier, in basis points. */
export function tierSavingBps(tiers: readonly PriceTier[], tier: PriceTier): number | null {
  const base = sortTiers(tiers)[0]
  if (!base || base.unitPriceMinor <= 0 || base === tier) return null
  const diff = base.unitPriceMinor - tier.unitPriceMinor
  if (diff <= 0) return null
  return Math.round((diff / base.unitPriceMinor) * 10_000)
}

/**
 * The next tier up and how much more the buyer must order to reach it.
 * Powers the "add 40 more bags and save 6%" nudge, which is the single most
 * effective way to explain bulk pricing to someone who has never seen a tier table.
 */
export function nextTierNudge(
  tiers: readonly PriceTier[],
  qty: number,
): { tier: PriceTier; addQty: number; savingBps: number } | null {
  const sorted = sortTiers(tiers)
  const current = resolveTier(sorted, qty)
  const next = sorted.find((t) => t.minQty > qty)
  if (!next) return null
  const currentPrice = current?.unitPriceMinor ?? sorted[0]?.unitPriceMinor
  if (currentPrice == null || next.unitPriceMinor >= currentPrice) return null
  return {
    tier: next,
    addQty: next.minQty - qty,
    savingBps: Math.round(((currentPrice - next.unitPriceMinor) / currentPrice) * 10_000),
  }
}
